import { useProgressBar } from '@hooks/helpers/useProgressBar';
import styles from './styles.module.scss';
import ProgressBar from '@components/progress_bar';
import { useForm } from '@hooks/helpers/useForm';
import { FormProvider, useWatch } from 'react-hook-form';
import { apiContactService, apiScheduleService, apiTransactionsService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { eTransferRequestMoneyProgressSteps } from '@const/progress_bar_steps';
import { useEffect, useMemo, useState } from 'react';
import { ESchedulePaymentType, EFundsOperation } from '@enum';
import { useApiQuery } from '@hooks/api/useApiQuery';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionType,
    TContact
} from '@xeppt/xeppt-sdk/types';
import ChooseMethodSection from '@sections/send_and_request/steps/choose_methods/method';
import { useTranslation } from 'react-i18next';
import { EContactType, TCreateContact } from '@xeppt/xeppt-sdk/types/wallet';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { useUserContext } from '@hooks/context/useUserContext';
import { EScheduleFrequency } from '@xeppt/xeppt-sdk/types/schedule';
import Button from '@components/common/button';
import Typography from '@components/common/typography';
import Recipient from '@sections/send_and_request/steps/recipient';
import Amount from '@sections/send_and_request/steps/amount';
import Schedule from '@sections/send_and_request/steps/schedule';
import Summary from '@sections/send_and_request/steps/summary';
import Success from '@sections/send_and_request/steps/success';
import Requester from '@sections/send_and_request/steps/requester';
import { routes } from '@const/routes';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useNavigate } from 'react-router-dom';
import { setToCurrentHour, isSameDayAsToday } from '@utils/date';

const defaultPaymentData = {
    paymentType: ESchedulePaymentType.PAYMENTS_DATE,
    contact: '',
    amount: undefined,
    isSchedule: false,
    account: '',
    message: '',
    paymentFrequency: EScheduleFrequency.ONCE,
    next_payment_at: new Date(),
    number_of_payments: 1,
    isInfinitePayment: false,
    expires_in: 30,
    new_contact: {
        first_name: '',
        last_name: '',
        phone: undefined,
        email: ''
    }
};

const RequestMoneySection = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('sections', {
        keyPrefix: 'send_and_request.send_money'
    });
    const [type, contact_id, schedule_id] = useQueryParams(['type', 'contact_id', 'id']);
    const form = useForm({ defaultValues: defaultPaymentData });
    const { refetchAccount } = useUserContext();
    const { requestSuccessLocale, requestErrorLocale, submitLocale } = useLocales();
    const [sendMethod, setSendMethod] = useState<
        Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>
    >(EFundsOperation.WALLET_DEBIT_REQUEST);
    const [refetchFeeTrigger, setRefetchFeeTrigger] = useState(0);
    const [contactsList, setContactsList] = useState<TContact[]>([]);
    const [isMethodChosen, setIsMethodChosen] = useState(false);
    const watchedFrequency = useWatch({ name: 'paymentFrequency', control: form.control });
    const watchedContactId = useWatch({ name: 'contact', control: form.control });
    const isSchedule = useWatch({ name: 'isSchedule', control: form.control });
    const { handleBack } = useNavigationContext();
    const [chosenContact, setChosenContact] = useState<TContact>();
    const [isNewContact, setIsNewContact] = useState(false);
    const [isCustomContact, setIsCustomContact] = useState(false);

    useEffect(() => {
        setChosenContact(contactsList.find((item) => item.id === watchedContactId));
    }, [watchedContactId, contactsList]);

    const isBank = useMemo(
        () =>
            sendMethod === EFundsOperation.EFT_CREDIT_WITHDRAWAL ||
            sendMethod === EFundsOperation.EFT_DEBIT_DEPOSIT,
        [sendMethod]
    );

    const isWallet = useMemo(
        () =>
            sendMethod === EFundsOperation.WALLET_CREDIT_TRANSFER ||
            sendMethod === EFundsOperation.WALLET_DEBIT_REQUEST,
        [sendMethod]
    );

    const isETransfer = useMemo(
        () =>
            sendMethod === EFundsOperation.E_TRANSFER_CREDIT_TRANSFER ||
            sendMethod === EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
        [sendMethod]
    );

    const isEps = useMemo(() => sendMethod === EFundsOperation.EPS_DEBIT_DEPOSIT, [sendMethod]);

    // A money request is gross-keyed: the payer is charged the stored/keyed amount and the
    // backend derives net + fee from it (CalculateFundsFromCharged), both immediately and on
    // each scheduled run. True for an e-Transfer request, or a wallet request to a known XEPPT
    // (internal) payer — the only request that routes to FundsRequestWallet. Drives whether the
    // scheduled / updated amount stores the GROSS (amount_result) vs the NET (amount).
    const isMoneyRequest = useMemo(
        () =>
            sendMethod === EFundsOperation.E_TRANSFER_DEBIT_REQUEST ||
            (sendMethod === EFundsOperation.WALLET_DEBIT_REQUEST &&
                chosenContact?.type === EContactType.INTERNAL),
        [sendMethod, chosenContact]
    );

    const steps = useMemo(() => {
        return eTransferRequestMoneyProgressSteps
            .filter((item) => {
                if (!isSchedule) {
                    // if (sendMethod !== EFundsOperation.E_TRANSFER_DEBIT_REQUEST) {
                    //     return item?.id !== '1' && item?.id !== '4';
                    // }
                    // return item?.id !== '4';
                    return item?.id !== '1' && item?.id !== '4';
                }
                // if (sendMethod !== EFundsOperation.E_TRANSFER_DEBIT_REQUEST) {
                //     return item?.id !== '1';
                // }
                // return true;
                return item?.id !== '1';
            })
            .map((item) => {
                if (item?.id === '2') {
                    if (isBank) return { ...item, title: 'Bank account' };
                    if (isWallet) return { ...item, title: 'Recipient' };
                    if (isETransfer) return { ...item, title: 'Recipient' };
                    if (isEps) return { ...item, title: 'Card' };
                    return item;
                }
                return item;
            });
    }, [
        eTransferRequestMoneyProgressSteps,
        isSchedule,
        sendMethod,
        isMethodChosen,
        isBank,
        isWallet,
        isETransfer,
        isEps
    ]);

    const { availableStep, prevStep, nextStep, currentStep, changeStep } = useProgressBar(steps, [
        form.watch(),
        sendMethod
    ]);
    const { data: contacts, handleRequest: refetchContacts } = useApiQuery({
        method: () => apiContactService.getContacts()
    });

    useEffect(() => {
        if (type) {
            //@ts-ignore
            setSendMethod(type as EFundsOperation);
            setIsMethodChosen(true);
        }
        if (contact_id) {
            form.setValue('contact', contact_id);
        }
    }, [type, contact_id, contactsList]);

    useEffect(() => {
        if (schedule_id) {
            changeStep('3');
        } else {
            // if (sendMethod === EFundsOperation.E_TRANSFER_DEBIT_REQUEST) {
            //     changeStep('1');
            // } else {
            //     changeStep('2');
            // }
            changeStep('2');
        }
    }, [sendMethod, schedule_id]);

    const { handleRequest: handleRequestMoneyByETransfer, isLoading: isETransferRequestLoading } =
        useApiMutation({
            method: () => {
                const values = form.getValues();
                return apiTransactionsService.requestMoneyETransfer({
                    contact_id: values.contact,
                    // Send the GROSS (amount_result = "Amount charged to payer") so the backend
                    // receives the exact amount to charge the payer, matching the fee model.
                    // amount_result is a registered field but absent from the form's typed
                    // defaultValues, so cast to read it (same pattern as the Amount step).
                    amount: Number((values as any).amount_result),
                    message: values.message,
                    method: 'email'
                });
            },
            onSuccess: () => {
                requestSuccessLocale('request_money');
                nextStep();
                refetchAccount();
            },
            onError: requestErrorLocale
        });

    const getTransactionMethod = () => {
        switch (sendMethod) {
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return ETransactionMethod.WALLET;
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return ETransactionMethod.EFT;
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return ETransactionMethod.EPS;
        }
    };

    const getTransactionAction = () => {
        switch (sendMethod) {
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return ETransactionAction.REQUEST;
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return ETransactionAction.DEPOSIT;
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return ETransactionAction.DEPOSIT;
        }
    };

    useApiQuery({
        method: () => apiScheduleService.getSchedule(schedule_id as string),
        condition: !!schedule_id,
        isInitialRequest: false,
        onSuccess: (data) => {
            form.reset({
                paymentType: ESchedulePaymentType.PAYMENTS_DATE,
                contact: data.method === ETransactionMethod.WALLET ? data.reference_id : undefined,
                //@ts-ignore
                amount: data.amount.toFixed(2),
                account: data.method === ETransactionMethod.EFT ? data.reference_id : undefined,
                paymentFrequency: data.frequency,
                next_payment_at: data.next_payment_at,
                number_of_payments: data.remaining_payments,
                isInfinitePayment: data.remaining_payments === -1,
                isSchedule: true
            });
            setRefetchFeeTrigger((val) => val + 1);
        },
        deps: [schedule_id]
    });

    const { handleRequest: createSchedulePayment, isLoading: isScheduleLoading } = useApiMutation({
        method: () => {
            const values = form.getValues();
            const {
                paymentFrequency,
                amount,
                number_of_payments,
                next_payment_at,
                isInfinitePayment,
                contact,
                account
            } = values;
            return apiScheduleService.createSchedule({
                // Store the GROSS for money requests (amount_result = "Amount charged to payer")
                // so each scheduled run charges the payer the gross and the backend derives
                // net + fee from it — matching the immediate request path. Non-request schedules
                // (send / load / withdraw / pay-bill) keep the net-keyed amount.
                amount: isMoneyRequest
                    ? Number((values as any).amount_result || 0)
                    : Number(amount || 0),
                next_payment_at: setToCurrentHour(next_payment_at),
                frequency: paymentFrequency,
                number_of_payments: isInfinitePayment ? -1 : Number(number_of_payments),
                reference_id:
                    sendMethod === EFundsOperation.EFT_DEBIT_DEPOSIT ||
                    sendMethod === EFundsOperation.EPS_DEBIT_DEPOSIT
                        ? account
                        : contact,
                method: getTransactionMethod() as ETransactionMethod,
                action: getTransactionAction() as ETransactionAction
            });
        },
        onSuccess: () => {
            requestSuccessLocale('create_schedule_payment');
            nextStep();
            refetchAccount();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: updateSchedulePayment, isLoading: isUpdateScheduleLoading } =
        useApiMutation({
            method: () => {
                const values = form.getValues();
                const {
                    paymentFrequency,
                    amount,
                    number_of_payments,
                    next_payment_at,
                    isInfinitePayment
                } = values;
                return apiScheduleService.updateSchedule(schedule_id as string, {
                    // Keep the GROSS for money requests, same as create (the executor charges
                    // the payer the stored amount and derives net + fee from it).
                    amount: isMoneyRequest
                        ? Number((values as any).amount_result || 0)
                        : Number(amount || 0),
                    next_payment_at: setToCurrentHour(next_payment_at),
                    frequency: paymentFrequency,
                    number_of_payments: isInfinitePayment ? -1 : Number(number_of_payments)
                });
            },
            onSuccess: () => {
                requestSuccessLocale('update_schedule_payment');
                nextStep();
                refetchAccount();
            },
            onError: requestErrorLocale
        });

    const { handleRequest: handleRequestMoneyByWallet, isLoading: isWalletRequestLoading } =
        useApiMutation({
            method: () => {
                const values = form.getValues();
                return apiTransactionsService.requestMoneyWallet({
                    contact_id: values.contact,
                    // Send the GROSS (amount_result = "Amount charged to payer") so the backend
                    // receives the exact amount to charge the payer, matching the fee model.
                    amount: Number((values as any).amount_result),
                    message: values.message
                });
            },
            onSuccess: () => {
                requestSuccessLocale('request_money');
                nextStep();
                refetchAccount();
            },
            onError: requestErrorLocale
        });

    const { handleRequest: handleRequestMoneyByEft, isLoading: isEftRequestLoading } =
        useApiMutation({
            method: () => {
                const values = form.getValues();
                return apiTransactionsService.requestMoneyEft({
                    id: values.account,
                    amount: Number(values.amount)
                });
            },
            onSuccess: () => {
                requestSuccessLocale('request_money');
                nextStep();
                refetchAccount();
            },
            onError: requestErrorLocale
        });

    const { handleRequest: handleRequestMoneyByEps, isLoading: isEpsRequestLoading } =
        useApiMutation({
            method: () => {
                const values = form.getValues();
                return apiTransactionsService.requestMoneyEps({
                    id: values.account,
                    amount: Number(values.amount)
                });
            },
            onSuccess: () => {
                requestSuccessLocale('request_money');
                nextStep();
                refetchAccount();
            },
            onError: requestErrorLocale
        });

    const { handleRequest: handleSaveContact, isLoading: isSaveContactLoading } = useApiMutation({
        method: () => {
            return apiContactService.addContact(form.getValues().contact as string);
        },
        onSuccess: (data) => {
            setIsNewContact(false);
            form.setValue('contact', data.id);
            setChosenContact(data);
            onSubmit(data);
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleCreateContact, isLoading: isCreateContactLoading } =
        useApiMutation({
            method: () => {
                const values = form.getValues().new_contact;
                const data: TCreateContact = {
                    first_name: values?.first_name,
                    last_name: values?.last_name,
                    phone: values?.phone || undefined,
                    email: values?.email
                };
                return apiContactService.createContact(data);
            },
            onSuccess: (data) => {
                setIsCustomContact(false);
                form.setValue('contact', data.id);
                setChosenContact(data);
                onSubmit(data);
            },
            onError: requestErrorLocale
        });

    const {
        handleRequest: handleRequestMoneyByWalletFromExternal,
        isLoading: isWalletExternalRequestLoading
    } = useApiMutation({
        method: () => {
            const values = form.getValues();
            return apiTransactionsService.createPaymentIntent({
                contact_id: values.contact,
                // Payment-intent path (external / non-XEPPT payer) — NOT the gross-keyed
                // FundsRequestWallet. This backend still keys the net; leave as net until the
                // payment-intent fee model is migrated in its own slice.
                amount: Number(values.amount),
                message: values.message,
                currency: 'CAD',
                type: ETransactionType.DEBIT,
                expires_in: Number(values.expires_in)
            });
        },
        onSuccess: () => {
            requestSuccessLocale('request_money');
            nextStep();
            refetchAccount();
        },
        onError: requestErrorLocale
    });

    const onSubmit = (newContact?: TContact) => {
        if (schedule_id) {
            updateSchedulePayment(undefined);
        } else {
            if (
                (watchedFrequency !== EScheduleFrequency.ONCE ||
                    !isSameDayAsToday(form.getValues().next_payment_at)) &&
                isSchedule
            ) {
                createSchedulePayment(undefined);
            } else {
                switch (sendMethod) {
                    case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                        handleRequestMoneyByETransfer(undefined);
                        break;
                    case EFundsOperation.WALLET_DEBIT_REQUEST:
                        {
                            const contact = newContact || chosenContact;
                            if (contact) {
                                // Gross-keyed FundsRequestWallet ONLY for a KNOWN XEPPT (internal)
                                // payer — matches the amount/summary isMoneyRequest scoping
                                // (=== INTERNAL). External / custom / unresolved payers go to the
                                // net-keyed payment-intent backend.
                                if (contact.type === EContactType.INTERNAL) {
                                    handleRequestMoneyByWallet(undefined);
                                } else {
                                    handleRequestMoneyByWalletFromExternal(undefined);
                                }
                            }
                        }
                        break;
                    case EFundsOperation.EFT_DEBIT_DEPOSIT:
                        handleRequestMoneyByEft(undefined);
                        break;
                    case EFundsOperation.EPS_DEBIT_DEPOSIT:
                        handleRequestMoneyByEps(undefined);
                        break;
                }
            }
        }
    };

    useEffect(() => {
        if (contacts) {
            setContactsList(contacts);
        }
    }, [sendMethod, contacts]);

    const handleNextStep = (onSuccess: () => void) => {
        form.trigger().then((isValid) => {
            if (isValid) {
                onSuccess();
            }
        });
    };

    useEffect(() => {
        scrollTo(0, 0);
    }, [currentStep, sendMethod]);

    const renderContent = () => {
        switch (currentStep?.id) {
            case '1':
                return <Requester isRequest onSubmit={() => handleNextStep(nextStep)} />;
            case '2':
                return (
                    <Recipient
                        onSubmit={() => handleNextStep(nextStep)}
                        contactsList={contactsList || []}
                        method={sendMethod}
                        onChangeNewContact={(val) => setIsNewContact(val)}
                        onChangeCustomContact={(val) => setIsCustomContact(val)}
                    />
                );
            case '3':
                return (
                    <Amount
                        isEdit={!!schedule_id}
                        onPrev={prevStep}
                        method={sendMethod}
                        contactList={contactsList || []}
                        refetchFeeTrigger={refetchFeeTrigger}
                        chosenContact={chosenContact}
                        isCustomContact={isCustomContact || isNewContact}
                        onSubmit={() =>
                            handleNextStep(() => (isSchedule ? nextStep() : changeStep('5')))
                        }
                    />
                );
            case '4':
                return <Schedule onPrev={prevStep} onSubmit={() => handleNextStep(nextStep)} />;
            case '5':
                return (
                    <Summary
                        isEdit={!!schedule_id}
                        onPrev={prevStep}
                        onSubmit={() =>
                            handleNextStep(() => {
                                console.log({ isCustomContact, isNewContact });
                                if (isCustomContact) {
                                    handleCreateContact(undefined);
                                } else if (isNewContact) {
                                    handleSaveContact(undefined);
                                } else {
                                    onSubmit();
                                }
                            })
                        }
                        contactList={contactsList || []}
                        isLoading={
                            isScheduleLoading ||
                            isWalletRequestLoading ||
                            isUpdateScheduleLoading ||
                            isEftRequestLoading ||
                            isETransferRequestLoading ||
                            isEpsRequestLoading ||
                            isWalletExternalRequestLoading ||
                            isCreateContactLoading ||
                            isSaveContactLoading
                        }
                        method={sendMethod}
                        isRequest
                        isCustomContact={isCustomContact}
                    />
                );
            case '6':
                return (
                    <Success
                        onReset={() => {
                            form.reset(defaultPaymentData);
                            changeStep('1');
                            navigate(routes.send_and_request_request);
                            refetchContacts();
                        }}
                        isSchedule={isSchedule}
                        method={sendMethod}
                        isExternal={isCustomContact || isNewContact}
                    />
                );
        }
    };

    const renderTitle = () => {
        switch (sendMethod) {
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return 'request_internal';
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return 'request_e_transfer';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return 'request_bank';
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return 'request_eps';
            default:
                return 'title_request';
        }
    };

    return (
        <motion.div {...pageAnimation} className={styles.main_wrapper}>
            {isMethodChosen ? (
                <>
                    <div className={styles.header}>
                        <Button
                            className={styles.back}
                            leftIcon="arrow_left"
                            variant="text-gray"
                            onClick={() => {
                                if (isMethodChosen) {
                                    form.reset(defaultPaymentData);
                                    setIsMethodChosen(false);
                                    changeStep('1');
                                } else {
                                    handleBack(routes.send_and_request);
                                }
                            }}>
                            {submitLocale('back')}
                        </Button>
                        <Typography
                            variant="h4"
                            dangerouslySetInnerHTML={{ __html: t(renderTitle()) }}
                        />
                    </div>
                    <div className={styles.content_wrapper}>
                        <ProgressBar
                            disabled={!!schedule_id}
                            steps={steps}
                            availableStep={availableStep}
                            currentStep={currentStep}
                            className={styles.progress}
                            changeStep={changeStep}
                        />
                        <div className={styles.body_wrapper}>
                            <FormProvider {...form}>{renderContent()}</FormProvider>
                        </div>
                    </div>
                </>
            ) : (
                <ChooseMethodSection
                    method={sendMethod}
                    onChangeMethod={(val) => setSendMethod(val)}
                    onSubmit={() => setIsMethodChosen(true)}
                    title={t('title_request')}
                    type="request"
                    styles={styles}
                />
            )}
        </motion.div>
    );
};

export default RequestMoneySection;
